import { useTrackEvent } from "libs/analytics";
import { VIDEO_EDITOR_EXPORT } from "../../constants/Events";
import CreativeEditorSDK from "@cesdk/cesdk-js";
import { CreativeBaseEditorModal } from "../photo-editor/creative-base-editor";
import { useState, useRef, useEffect } from "react";

export function CreativeVideoEditorModal(props: {
  show: boolean;
  video: string;
  onClose(): void;
  onSave(blob: Blob): void;
  progress: number | null;
  saveButtonText?: string;
  message?: string;
  isSaving?: boolean;
}) {
  const trackEvent = useTrackEvent();
  const [hasChanges, setHasChanges] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (props.show) {
      setHasChanges(false);
      setIsExporting(false);
    }
  }, [props.show]);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!props.show && unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, [props.show]);

  if (!props.show) {
    return null;
  }

  return (
    <CreativeBaseEditorModal
      style={
        {
          "--ubq-InspectorBar-background": "var(--ubq-canvas)",
        } as React.CSSProperties
      }
      title="Video Editor"
      onClose={props.onClose}
      progress={
        props.progress
          ? {
              label: "Saving...",
              percentage: props.progress,
            }
          : undefined
      }
      saveButtonText={props.saveButtonText}
      message={props.message}
      isSaving={props.isSaving || isExporting}
      saveDisabled={!hasChanges}
      onSave={async (instance: CreativeEditorSDK) => {
        // Immediately show loading state
        setIsExporting(true);

        try {
          const engine = instance.engine;
          let videoBlob: Blob;

          if (hasChanges) {
            const page = engine.scene.getCurrentPage();
            if (!page) {
              throw new Error("No current page found");
            }

            try {
              const progressCallback = () => {
                // Progress tracking available here if needed
              };

              videoBlob = await engine.block.exportVideo(
                page,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                "video/mp4" as any,
                progressCallback,
                {}
              );
            } catch {
              // Create archive to preserve changes
              await engine.scene.saveToArchive();

              // Return original video as fallback
              const response = await fetch(props.video);
              videoBlob = await response.blob();
            }
          } else {
            // No changes made, return original video
            const response = await fetch(props.video);
            videoBlob = await response.blob();
          }

          trackEvent({ eventName: VIDEO_EDITOR_EXPORT });
          props.onSave(videoBlob);
        } catch (error) {
          console.error("VideoEditor onSave - Export failed:", error);
          throw error;
        } finally {
          setIsExporting(false);
        }
      }}
      onInit={async (instance) => {
        const cleanup = await initVideoEditorUIConfig(
          instance,
          props.video,
          setHasChanges
        );
        unsubscribeRef.current = cleanup;
      }}
    />
  );
}

export async function initVideoEditorUIConfig(
  instance: CreativeEditorSDK,
  videoUri: string,
  setHasChanges: (hasChanges: boolean) => void
) {
  await instance.addDefaultAssetSources();
  await instance.addDemoAssetSources({ sceneMode: "Video" });

  // Disable placeholder and preview features
  instance.feature.enable("ly.img.placeholder", false);
  instance.feature.enable("ly.img.preview", false);

  // Create scene from video
  await instance.engine.scene.createFromVideo(videoUri);

  // Get the current page to subscribe to changes
  const page = instance.engine.scene.getCurrentPage();
  if (!page) {
    return () => {};
  }

  // Add a delay to ignore initial loading events
  let isInitializing = true;
  setTimeout(() => {
    isInitializing = false;
  }, 2000); // 2 second delay to ignore loading events

  // Subscribe to block events to detect changes
  const unsubscribe = instance.engine.event.subscribe([page], (events) => {
    // Ignore events during initial loading
    if (isInitializing) {
      return;
    }

    const hasUpdates = events.some((event) => event.type === "Updated");
    const hasCreations = events.some((event) => event.type === "Created");
    const hasDeletions = events.some((event) => event.type === "Destroyed");

    if (hasUpdates || hasCreations || hasDeletions) {
      setHasChanges(true);
    }
  });

  return () => {
    unsubscribe();
  };
}
